// What state a task is in, where it sits, and how urgent it is. Pure functions
// of the task and the clock, shared by the Plan tab, the Calendar tab and the
// morning summary's copy in Functions.

import type { DateStr, Task, TimeStr } from '@/types'
import { addDays, dateOf, dayDiff, startOfWeek, timeOf } from './dates'

/** The moment the rules judge by: the local date and clock time. */
export interface Now {
  date: DateStr
  time: TimeStr
}

export function nowOf(d: Date = new Date()): Now {
  return { date: dateOf(d), time: timeOf(d) }
}

type Judged = Pick<Task, 'kind' | 'done' | 'plannedDate' | 'dueDate' | 'dueTime' | 'waiting' | 'followUpDate' | 'followUpTime' | 'weekOf'>

/** A one-off is open until ticked; repeats and routines stay open, their ticks kept as completions. */
export function isOpen(t: Pick<Task, 'kind' | 'done'>): boolean {
  return t.kind !== 'once' || !t.done
}

/** A routine's current occurrence. */
export interface RoutineWeek {
  /** The Monday of the week the occurrence belongs to. */
  weekOf: DateStr
  plannedDate: DateStr | null
  /** This week's occurrence is ticked, so the stored week is next week's. */
  doneThisWeek: boolean
}

/**
 * Where a routine stands now, from its stored fields alone. A week that has
 * ended without a tick rolls into this one on the same weekday (or stays
 * unplaced), so nothing depends on anyone opening the app.
 */
export function routineWeek(t: Pick<Task, 'weekOf' | 'plannedDate'>, today: DateStr): RoutineWeek {
  const monday = startOfWeek(today)
  const stored = t.weekOf ?? monday
  if (stored > monday) return { weekOf: stored, plannedDate: t.plannedDate, doneThisWeek: true }
  const shift = dayDiff(stored, monday)
  return { weekOf: monday, plannedDate: t.plannedDate ? addDays(t.plannedDate, shift) : null, doneThisWeek: false }
}

/**
 * The day an open task sits on, or null when it belongs in the tray. A waiting
 * task sits on its follow-up day; a routine on this week's day (none once
 * ticked this week); a one-off only on its Do it on day, never its due date
 * alone (it waits in No date, its due date on the card); a repeat on its Do it
 * on day, else the due date its pattern brings it round on.
 */
export function placeDay(t: Judged, today: DateStr): DateStr | null {
  if (!isOpen(t)) return null
  if (t.waiting && t.followUpDate) return t.followUpDate
  if (t.kind === 'routine') {
    const r = routineWeek(t, today)
    return r.doneThisWeek ? null : r.plannedDate
  }
  return t.kind === 'repeat' ? (t.plannedDate ?? t.dueDate) : t.plannedDate
}

/** The clock time a task keeps on the day it sits on: its due time there, or a waiting task's follow-up time. */
export function timeOnDay(t: Judged, day: DateStr): TimeStr | null {
  if (t.waiting && t.followUpDate === day && t.followUpTime) return t.followUpTime
  if (t.dueDate === day && t.dueTime) return t.dueTime
  return null
}

/** Orange the day before and on the day; red from the moment it passes. */
export type Urgency = 'soon' | 'late' | null

function urgencyOf(date: DateStr | null, time: TimeStr | null, now: Now): Urgency {
  if (!date) return null
  if (date < now.date) return 'late'
  if (date === now.date) return time && time <= now.time ? 'late' : 'soon'
  if (date === addDays(now.date, 1)) return 'soon'
  return null
}

/** How close an open task's due date is. A waiting task's deadline counts too. */
export function dueUrgency(t: Judged, now: Now): Urgency {
  if (!isOpen(t) || t.kind === 'routine') return null
  return urgencyOf(t.dueDate, t.dueTime, now)
}

/** How close a waiting task's follow-up is. */
export function followUpUrgency(t: Judged, now: Now): Urgency {
  if (!isOpen(t) || !t.waiting) return null
  return urgencyOf(t.followUpDate, t.followUpTime, now)
}

export function isOverdue(t: Judged, now: Now): boolean {
  return dueUrgency(t, now) === 'late'
}

/** The worst of the two, for the ring and the card's tint. */
export function urgency(t: Judged, now: Now): Urgency {
  const a = dueUrgency(t, now)
  const b = followUpUrgency(t, now)
  if (a === 'late' || b === 'late') return 'late'
  if (a === 'soon' || b === 'soon') return 'soon'
  return null
}

/** An open task whose day has passed: it waits in Not done until it is ticked or moved. */
export function isNotDone(t: Judged, today: DateStr): boolean {
  const day = placeDay(t, today)
  return !!day && day < today
}

/** A waiting task whose follow-up day has come: the morning summary's "to follow up". */
export function needsFollowUp(t: Judged, today: DateStr): boolean {
  return isOpen(t) && t.waiting && !!t.followUpDate && t.followUpDate <= today
}
